import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

import BaseModule from '../../../javascripts/helpers/baseModule';
import showElement from '../../../javascripts/dom/showElement';
import hideElement from '../../../javascripts/dom/hideElement';

export default class Navigation extends BaseModule {
  constructor(element) {
    super(element, 'navigation', [
      {
        selector: 'inital',
        id: 'inital'
      },
      {
        selector: 'menu',
        id: 'menu'
      },
      {
        selector: 'menu-btn',
        id: 'menuBtns'
      },
      {
        selector: 'primary',
        id: 'primary'
      },
      {
        selector: 'back-btn',
        id: 'backBtn'
      },
      {
        selector: 'level-2-btn',
        id: 'level2Btns'
      },
      {
        selector: 'level-3-btn',
        id: 'level3Btns'
      },
      {
        selector: 'level-2-list',
        id: 'level2List'
      },
      {
        selector: 'level-3-list',
        id: 'level3List'
      }
    ]);

    // set initial states
    this.isMobile = this.isMobile || false;
    this.activeLevel = this.activeLevel || 0;
    this.activePath = this.activePath || []; // example: [{ btn: HTMLElement, list: HTMLElement }, ...]
    this.initalPath = this.initalPath || { list2Btn: null, list3Btn: null };
    this.backBtnConfig = this.backBtnConfig || {
      currentBtn: '',
      prevBtn: ''
    };
  }

  static openNavClass = 'navigation--open-nav';

  static stickyNavClass = 'navigation--is-sticky';

  static mobileNavigationMQ = window.matchMedia('(min-width: 1024px)');

  /**
   * @returns {boolean} - whether menu is open
   */
  get hasClosedMenu() {
    return this.elements.menu.getAttribute('aria-expanded') !== 'true';
  }

  /**
   * get list element from btn element
   * @param {HTMLElement} btnElem -  btn element
   * @param {Number} btnLevel - 2 || 3
   * @returns {void}
   */
  getList(btnElem, btnLevel) {
    const listId = btnElem.dataset.navBtnId;
    const btnList = btnLevel === 2 ? this.elements.level2List : this.elements.level3List;
    return btnList.asList.find((listElem) => listElem.dataset.navListId === listId);
  }

  /**
   * Hide lists
   * @param {number} hideMinLevel - hide only list of higher level
   * @returns {void}
   */
  hideLists(hideMinLevel = false) {
    this.activePath.forEach((pathObject, index) => {
      if (hideMinLevel && index + 1 < hideMinLevel) return;
      pathObject.btn.removeAttribute('data-nav-btn-active');
      hideElement(pathObject.list);
    });
  }

  /**
   * Show list matching btn
   * @param {HTMLElement} btn - btn element
   * @param {Number} level - 2 || 3
   * @returns {void}
   */
  showList(btn, level) {
    const list = this.getList(btn, level);
    showElement(list);
    btn.setAttribute('data-nav-btn-active', '');

    // update active path
    this.activePath[level - 2] = { btn, list };
  }

  /**
   * set active level
   * @param {Number} level - between 0 & 3
   * @returns {void}
   */
  setActiveLevel(level) {
    // update level
    this.activeLevel = level;
    this.elements.primary.setAttribute('data-nav-level', level);
  }

  /**
   * reset inital path
   * @returns {void}
   */
  setInitialPath() {
    // get inital path
    const { list2Btn: activeList2Btn, list3Btn: activeList3Btn } = this.initalPath;

    // set active level (sum of active btns)
    this.setActiveLevel([activeList2Btn, activeList3Btn].reduce((accumulator, currentValue) => (currentValue ? accumulator + 1 : accumulator), 1));
    // set as active path
    this.setActivePath(activeList2Btn || null, activeList3Btn || null);
  }

  setActivePath(level2Btn = null, level3Btn = null) {
    // default maxLevel
    let maxLevel = 1;

    // hide prev active lists & btns
    if (!level3Btn || this.isMobile) {
      // hide all (for mobile & and level 2)
      this.hideLists();
    } else if (level3Btn) {
      // hide only lists/btns above active level
      this.hideLists(2);
    }

    // set new active path
    if (level2Btn || level3Btn) {
      // increase maxLevel
      maxLevel = level3Btn ? 3 : 2;

      // show level 2 btn & list
      if (level2Btn) this.showList(level2Btn, 2);

      // show level 3 btn & list
      if (level3Btn) this.showList(level3Btn, 3);
    } else {
      this.activePath = [];
    }

    // update back btn
    if (maxLevel === 2) {
      this.updateBackBtn(level2Btn, level2Btn);
    } else if (maxLevel > 2) {
      this.updateBackBtn(level3Btn, level2Btn);
    } else if (maxLevel < 2) {
      this.updateBackBtn('', '');
    }
  }

  /**
   * Update Back Btn & Back Btn Config
   * @param {HTMLElement} currentBtn - current Btn
   * @param {HTMLElement} prevBtn - previous btn
   * @returns {void}
   */
  updateBackBtn(currentBtn, prevBtn = false) {
    // replace label
    this.elements.backBtn.getElementsByTagName('span')[0].textContent = currentBtn.textContent;

    // update config
    this.backBtnConfig = {
      ...this.backBtnConfig,
      currentBtn,
      prevBtn: prevBtn || this.backBtnConfig.prevBtn
    };
  }

  /**
   * On "Back" click update active level & button labels
   * @param {Object} event - JS event
   * @returns {void}
   */
  onBackBtnClick(event) {
    event.preventDefault();
    const { prevBtn } = this.backBtnConfig;
    const newLevel = this.activeLevel - 1;

    // set active level
    this.setActiveLevel(this.activeLevel - 1);

    // set active path
    if (newLevel === 1) {
      this.setActivePath();
    } else if (newLevel === 2) {
      this.setActivePath(prevBtn);
    } else if (newLevel === 3) {
      this.setActivePath(null, prevBtn);
    }
  }

  /**
   * On Level Btn Click show active Btn List
   * @param {Object} event - JS event
   * @param {Number} level - 2 or 3
   * @returns {void}
   */
  onLevelClick(event, level) {
    event.preventDefault();

    // get button element
    const buttonElem = event.target.closest('[data-nav-btn-id]');

    // set active Level
    this.setActiveLevel(level);

    // set active path
    if (level === 2) {
      this.setActivePath(buttonElem);
    } else if (level === 3) {
      this.setActivePath(null, buttonElem);
      // console.log(buttonElemContent);
    }
  }

  /**
   * Open menu:
   * - set active level & initial path
   * - show menu content
   * @returns {void}
   */
  openMenu() {
    this.elements.self.classList.add(Navigation.openNavClass);

    showElement(this.elements.menu);

    disableBodyScroll(this.elements.menu);

    this.setInitialPath(1);

    this.elements.inital.setAttribute('aria-hidden', 'true');
    this.elements.menu.setAttribute('aria-expanded', 'true');
    this.elements.menuBtns.asList.forEach((menuBtn) => {
      menuBtn.setAttribute('aria-expanded', 'true');
    });
  }

  /**
   * Close menu:
   * - slide out menu overlay
   * - reset active level & path
   * - hide all of menu content
   * @returns {void}
   */
  closeMenu() {
    // disable body lock
    enableBodyScroll(this.elements.menu);

    // set aria attributes
    this.elements.inital.removeAttribute('aria-hidden');
    this.elements.menu.setAttribute('aria-expanded', 'false');
    this.elements.menuBtns.asList.forEach((menuBtn) => {
      menuBtn.setAttribute('aria-expanded', 'false');
    });

    // remove css class
    this.elements.self.classList.remove(Navigation.openNavClass);

    // reset active level
    this.setActiveLevel(0);

    // set active path back to inital path
    this.setActivePath(this.initalPath.list2Btn, this.activePath.list3Btn);

    setTimeout(() => {
      hideElement(this.elements.menu);
    }, 600);

    // get the height of the menu's inner content
    // const sectionHeight = this.elements.menu.scrollHeight;

    // // temporarily disable all css transitions for menu element
    // const menuTransition = this.elements.menu.style.transition;
    // this.elements.menu.style.transition = '';

    // on the next frame explicitly set the menu's height to its current pixel height
    // so we aren't transitioning out of 'auto'
    // requestAnimationFrame(() => {
    //   this.elements.menu.style.height = `${sectionHeight}px`;
    //   this.elements.menu.style.transition = menuTransition;

    //   // on the next frame have the menu transition to height: 0
    //   requestAnimationFrame(() => {
    //     this.elements.menu.style.height = '0px';

    //     // when the next css transition finishes
    //     this.elements.menu.addEventListener(
    //       'transitionend',
    //       (e) => {
    //         // set menu to hidden
    //         hideElement(this.elements.menu);

    //         // set aria attributes
    //         this.elements.inital.removeAttribute('aria-hidden');
    //         this.elements.menu.setAttribute('aria-expanded', 'false');
    //         this.elements.menuBtns.asList.forEach((menuBtn) => {
    //           menuBtn.setAttribute('aria-expanded', 'false');
    //         });

    //         // remove css class
    //         this.elements.self.classList.remove(Navigation.openNavClass);

    //         // reset active level
    //         this.setActiveLevel(0);

    //         // set active path back to inital path
    //         this.setActivePath(this.initalPath.list2Btn, this.activePath.list3Btn);

    //         // reset menu maxHeight
    //         if (this.elements.menu.style.maxHeight !== 'auto') {
    //           this.elements.menu.style.maxHeight = '100vh';
    //         }
    //       },
    //       {
    //         // de-register eventlistener again
    //         once: true
    //       }
    //     );
    //   });
    // });
  }

  /**
   * Control the navigation on scroll
   * @param {*} event - the inital event
   * @returns {void}
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.elements.inital.offsetHeight) {
      this.elements.self.classList.add(Navigation.stickyNavClass);
    } else {
      this.elements.self.classList.remove(Navigation.stickyNavClass);
    }
  }

  /**
   * Init functions add event listeners
   * and sets variables 'isMobile' and 'initalPath'
   * @returns {void}
   */
  init() {
    // set if mobile
    this.isMobile = !Navigation.mobileNavigationMQ.matches;

    // set initial path of current page
    this.initalPath = {
      list2Btn: (this.elements.level2Btns && this.elements.level2Btns.asList.find((btn) => btn.hasAttribute('data-nav-btn-active'))) || null,
      list3Btn: (this.elements.level3Btns && this.elements.level3Btns.asList.find((btn) => btn.hasAttribute('data-nav-btn-active'))) || null
    };

    // OnScroll Eventlistener
    window.addEventListener('scroll', throttle(this.onScroll.bind(this), 100));

    // Resize eventlistener
    window.addEventListener(
      'resize',
      debounce((e) => {
        // if desktop
        if (Navigation.mobileNavigationMQ.matches) {
          // if was open on mobile before
          if (this.isMobile && !this.hasClosedMenu) {
            // close menu to reset active elements
            this.closeMenu(this.elements);
          }
        }

        // check if mobile
        this.isMobile = !Navigation.mobileNavigationMQ.matches;
      }, 100)
    );

    // Event Listeners for Menu Buttons (to Toggle Menu)
    this.elements.menuBtns.asList.forEach((menuBtn) => {
      menuBtn.addEventListener('click', (event) => {
        // Tracking Code Integration
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'clickMenue'
        });
        event.preventDefault();
        if (this.hasClosedMenu) {
          this.openMenu(this.elements);
        } else {
          this.closeMenu(this.elements);
        }
      });
    });

    // Event Listeners for Navigation Buttons (to Show Next Level)

    if (this.elements.level2Btns) {
      this.elements.level2Btns.asList.forEach((levelBtn) => {
        levelBtn.addEventListener('click', (event) => {
          // Abrufen des Textes des angeklickten Elements
          const targetTitleText = event.currentTarget.innerText.trim() || 'no text available';

          // Tracking Code Integration
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'navigationItem',
            navigationLevel: 'two',
            targetTitle: targetTitleText
          });

          this.onLevelClick(event, 2);
        });
      });
    }

    if (this.elements.level3Btns) {
      this.elements.level3Btns.asList.forEach((levelBtn) => {
        levelBtn.addEventListener('click', (event) => {
          // Abrufen des Textes des angeklickten Elements
          const targetTitleText = event.currentTarget.innerText.trim() || 'no text available';

          // Tracking Code Integration
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'navigationItem',
            navigationLevel: 'three',
            targetTitle: targetTitleText
          });

          this.onLevelClick(event, 3);
        });
      });
    }

    const navigationLinks = document.querySelectorAll('.navigation__link');
    navigationLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const targetTitleText = event.currentTarget.innerText.trim() || 'no text available';
        // Finde das nächstgelegene Elternelement mit der Klasse --level-x
        const parentLevelElement = event.currentTarget.closest('[class*="--level-"]');
        let navigationLevel = 'unknown';
        if (parentLevelElement) {
          const levelClass = Array.from(parentLevelElement.classList).find((className) => className.includes('--level-'));
          if (levelClass) {
            [, navigationLevel] = levelClass.split('--level-');
          }
        }

        // Tracking Code für Links
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'navigationItem',
          navigationLevel,
          targetTitle: targetTitleText
        });
      });
    });

    // Event Listener for Back Button (back to level 2,3 )
    this.elements.backBtn.addEventListener('click', (event) => {
      this.onBackBtnClick(event);
    });

    // set the inital value of the "Animation On/Off" toggle & listen to changes
    const animationsEnabledToggle = this.elements.self.querySelector("input[name='animationsEnabled']");
    if (animationsEnabledToggle) {
      const animationsEnabled = localStorage.getItem('animationsEnabled');
      if (animationsEnabled === 'false') {
        animationsEnabledToggle.checked = false;
      } else {
        animationsEnabledToggle.checked = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      animationsEnabledToggle.addEventListener('change', (event) => {
        localStorage.setItem('animationsEnabled', event.target.checked);
        window.dispatchEvent(new CustomEvent('animationsEnabled', { detail: event.target.checked }));
      });
    }
  }
}
